/**
 * CustomDatagrid Component
 *
 * A responsive data grid that automatically switches between:
 * - Desktop: Traditional table view with sorting/filtering
 * - Mobile: Card-based layout optimized for touch
 *
 * USAGE:
 * ```tsx
 * <List>
 *   <CustomDatagrid
 *     primarySource="name"      // Field for card title on mobile
 *     secondarySource="email"   // Field for subtitle on mobile
 *     detailColumns={2}         // Number of columns in mobile detail grid
 *   >
 *     <TextField source="name" />
 *     <TextField source="email" />
 *     <DateField source="createdAt" />
 *     <EditButton />
 *   </CustomDatagrid>
 * </List>
 * ```
 */

import React from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useListContext, useTranslate, DatagridProps, RaRecord } from 'react-admin';
import CustomDataGridDesktop from './CustomDataGridDesktop';
import CustomDataGridMobile from './CustomDataGridMobile';
import NoDataMessage from './NoDataMessage';

interface CustomDataGridProps<RecordType extends RaRecord = any> extends DatagridProps<RecordType> {
  children: React.ReactNode;
  primarySource?: string;
  secondarySource?: string;
  detailColumns?: 2 | 3;
  disableMobileView?: boolean;
}

const CustomDatagrid = <RecordType extends RaRecord = any>({
  primarySource,
  secondarySource,
  detailColumns = 2,
  disableMobileView = false,
  expand,
  ...props
}: CustomDataGridProps<RecordType>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data = [], isLoading } = useListContext();
  const translate = useTranslate();

  // Show empty state when no data
  if (!isLoading && data.length === 0) {
    return (
      <NoDataMessage
        title={translate('common.no_data_title')}
        description={translate('common.no_data_description')}
      />
    );
  }

  if (isMobile && !disableMobileView) {
    return (
      <CustomDataGridMobile
        {...props}
        expand={expand}
        primarySource={primarySource}
        secondarySource={secondarySource}
        detailColumns={detailColumns}
      />
    );
  }

  return (
    <CustomDataGridDesktop
      {...props}
      expand={expand}
    />
  );
};

export default CustomDatagrid;
